import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { Badge } from "@/components/ui/badge";
import {
  autonomyStatusLabel,
  formatEligibleModes,
  ELIGIBILITY_MISSING_LABEL,
  ELIGIBILITY_LOADING_LABEL,
  ELIGIBILITY_ERROR_LABEL,
  type TruckEligibilityRow,
} from "@/lib/truck-eligibility";

/**
 * Latest AAM eligibility event for one tractor. Only the latest event matters,
 * so this reads the pre-resolved `truck_eligibility_current` view (one row per
 * tractor) rather than scanning the raw event table.
 */
export function TractorEligibilityCard({ tractorId }: { tractorId: string }) {
  // Eligibility rows are tenant-scoped by RLS, so the cache must be too:
  // without the tenant in the key a tenant switch would show the previous
  // tenant's record instead of re-entering the loading state.
  const { activeTenant } = useTenant();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["truck-eligibility-current", activeTenant, tractorId],
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("truck_eligibility_current" as never)
        .select(
          "tractor_id, status, allowed_modes, eligible_modes, reasons, constraint_tags, decided_by, revision, emitted_at, status_effective_at",
        )
        .eq("tractor_id", tractorId)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as unknown as TruckEligibilityRow | null;
    },
  });

  const rows: { label: string; value: React.ReactNode }[] = data
    ? [
        {
          label: "Autonomy Status",
          value: (
            <Badge variant="secondary">{autonomyStatusLabel(data.status)}</Badge>
          ),
        },
        { label: "Operating Mode", value: formatEligibleModes(data) },
        {
          label: "Reasons",
          value: data.reasons?.length ? data.reasons.join(", ") : "—",
        },
        {
          label: "Effective",
          value: data.status_effective_at
            ? format(new Date(data.status_effective_at), "MMM d, yyyy h:mm a")
            : "—",
        },
      ]
    : [];

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Eligible operations</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading ? (
          <p className="py-2 text-sm text-muted-foreground">
            {ELIGIBILITY_LOADING_LABEL}
          </p>
        ) : isError ? (
          <p className="py-2 text-sm text-muted-foreground">
            {ELIGIBILITY_ERROR_LABEL}
          </p>
        ) : !data ? (
          <div className="py-2">
            <p className="text-sm font-medium text-destructive">
              {ELIGIBILITY_MISSING_LABEL}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              No eligibility event received for this truck yet.
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {rows.map((r) => (
                <tr key={r.label} className="border-b last:border-0">
                  <td className="py-1.5 pr-3 text-muted-foreground">{r.label}</td>
                  <td className="py-1.5 text-right font-medium">{r.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}
